import { useMemo, useState } from 'react'
import { useSearchParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Inbox, Calendar, AlertOctagon, ChevronRight, Focus, Lock } from 'lucide-react'
import {
  Card,
  Chip,
  cn,
  priorityTone,
  statusTone,
} from '../design'
import { getMyDashboard, listMyStories, type MyStory } from '../api/me'
import { getAgenda, type AgendaItem } from '../api/agenda'
import { FocusPlanner } from './mywork/FocusPlanner'
import { useNow } from '../hooks/useNow'
import { useAuth } from '../auth/AuthContext'

/**
 * Personal cross-project home.
 * Stat tiles + filterable list of stories assigned to the current user across
 * all projects, next to today's agenda (the same /me/agenda feed as the
 * calendar). Focus mode opens the day as a paper planner (mywork/FocusPlanner).
 *
 * Filters mirror the iOS app (Models/Filters.swift): Focus = open stories due
 * today or overdue; This week; Doing; Overdue; Open; Done.
 */
const FOCUS_KEY = 'atlas.mywork.focus'

const FILTERS: Array<[string, string]> = [
  ['today',       'Focus'],
  ['this_week',   'This week'],
  ['in_progress', 'Doing'],
  ['overdue',     'Overdue'],
  ['all',         'Open'],
  ['done',        'Done'],
]

export default function MyWork() {
  const { user } = useAuth()
  const [params, setParams] = useSearchParams()
  const [focusMode, setFocusModeState] = useState(() => {
    try { return localStorage.getItem(FOCUS_KEY) === '1' } catch { return false }
  })
  const filter = focusMode ? 'today' : (params.get('filter') ?? 'today')
  // Ticks every 30s so the page — and "today" at midnight — stays current in an open tab.
  const now = useNow()
  const todayStr = ymd(now)

  const dashQ = useQuery({
    queryKey: ['me', 'dashboard'],
    queryFn: getMyDashboard,
    refetchInterval: 12_000,
    refetchOnWindowFocus: true,
  })
  const storiesQ = useQuery({
    queryKey: ['me', 'stories', filter, todayStr],
    queryFn: () => listMyStories({
      status_category: filter === 'in_progress' ? 'in_progress' : filter === 'done' ? 'done' : undefined,
      include_done: filter === 'done' ? 1 : 0,
      due_to: filter === 'today' ? todayStr : undefined,
      per_page: 100,
    }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })

  const filteredStories = useMemo(() => {
    const all = storiesQ.data ?? []
    const today = startOfDay(now)
    if (filter === 'today') return all.filter(s => s.due_date && new Date(s.due_date + 'T00:00:00') <= today)
    if (filter === 'overdue') return all.filter(s => s.due_date && new Date(s.due_date + 'T00:00:00') < today)
    if (filter === 'this_week') return all.filter(s => s.due_date && isThisWeek(s.due_date))
    if (filter === 'in_progress') return all.filter(s => s.status_category === 'in_progress')
    return all
  }, [storiesQ.data, filter, now])

  function setFilter(v: string) {
    const next = new URLSearchParams(params)
    if (v === 'today') next.delete('filter'); else next.set('filter', v)
    setParams(next, { replace: true })
  }

  function setFocusMode(on: boolean) {
    setFocusModeState(on)
    try { localStorage.setItem(FOCUS_KEY, on ? '1' : '0') } catch { /* private mode */ }
  }

  const storyList = (
    <Card padded={false}>
      {storiesQ.isLoading && <div className="px-5 py-10 text-center text-sm text-fg-subtle">Loading…</div>}
      {!storiesQ.isLoading && filteredStories.length === 0 && (
        <div className="px-5 py-10 text-center">
          <Inbox size={28} className="mx-auto text-fg-disabled" />
          <p className="text-sm text-fg-muted mt-2">
            {filter === 'today' ? 'Nothing due today or overdue.' : 'Nothing here.'}
          </p>
          <p className="text-xs text-fg-subtle mt-0.5">
            {filter === 'today' ? (focusMode ? 'Enjoy the clear runway.' : 'Pick something else from the tabs above.') : 'Try another filter.'}
          </p>
        </div>
      )}
      <ul className="divide-y divide-border">
        {filteredStories.map(s => <StoryRow key={s.id} story={s} />)}
      </ul>
    </Card>
  )

  if (focusMode) {
    return (
      <FocusPlanner
        stories={filteredStories}
        loading={storiesQ.isLoading}
        blocked={dashQ.data?.stats.blocked ?? 0}
        now={now}
        onExit={() => setFocusMode(false)}
      />
    )
  }

  return (
    <div className="mx-auto max-w-6xl px-6 py-6 space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">My Work</h1>
          <p className="text-sm text-fg-subtle mt-1">
            Stories assigned to {user?.name} across all projects.
          </p>
        </div>
        <button
          onClick={() => setFocusMode(true)}
          title="Just what's due now and today's schedule"
          className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border border-border text-fg-muted hover:bg-bg-muted"
        >
          <Focus size={12} /> Focus mode
        </button>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile
          label="Due today"
          value={dashQ.data?.stats.due_today ?? '—'}
          icon={<Calendar size={14} />}
          accent="info"
          onClick={() => setFilter('today')}
          active={filter === 'today'}
        />
        <StatTile
          label="This week"
          value={dashQ.data?.stats.due_week ?? '—'}
          icon={<Calendar size={14} />}
          accent="warning"
          onClick={() => setFilter('this_week')}
          active={filter === 'this_week'}
        />
        <StatTile
          label="Overdue"
          value={dashQ.data?.stats.overdue ?? '—'}
          icon={<AlertOctagon size={14} />}
          accent="danger"
          onClick={() => setFilter('overdue')}
          active={filter === 'overdue'}
        />
        <StatTile
          label="Blocked"
          value={dashQ.data?.stats.blocked ?? '—'}
          icon={<AlertOctagon size={14} />}
          accent="danger"
          onClick={() => setFilter('all')}
          active={filter === 'all'}
        />
      </div>

      <div className="grid gap-5 lg:grid-cols-3 items-start">
        <div className="lg:col-span-2 space-y-3 min-w-0">
          {/* Filter tabs */}
          <div className="flex items-center gap-2 text-sm flex-wrap">
            {FILTERS.map(([v, label]) => (
              <button
                key={v}
                onClick={() => setFilter(v)}
                className={cn(
                  'px-3 py-1.5 rounded-md text-xs font-medium',
                  filter === v
                    ? 'bg-accent text-accent-fg'
                    : 'bg-bg-elevated border border-border text-fg-muted hover:bg-bg-muted',
                )}
              >
                {label}
              </button>
            ))}
            <span className="ml-auto text-xs text-fg-subtle font-mono">
              {storiesQ.isLoading ? '…' : `${filteredStories.length} stories`}
            </span>
          </div>
          {storyList}
        </div>
        <TodayAgenda now={now} />
      </div>
    </div>
  )
}

// ─── components ──────────────────────────────────────────────────────────

function StatTile({
  label, value, icon, accent, onClick, active,
}: {
  label: string
  value: number | string
  icon: React.ReactNode
  accent: 'info' | 'warning' | 'danger' | 'success'
  onClick: () => void
  active?: boolean
}) {
  const accentBar = {
    info: 'bg-info', warning: 'bg-warning', danger: 'bg-danger', success: 'bg-success',
  } as const
  return (
    <button
      onClick={onClick}
      className={cn(
        'rounded-xl border bg-bg-elevated text-left overflow-hidden transition-all',
        active ? 'border-accent ring-2 ring-accent/30' : 'border-border hover:bg-bg-muted',
      )}
    >
      <div className={cn('h-1', accentBar[accent])} />
      <div className="px-4 py-3">
        <div className="flex items-center justify-between text-[11px] uppercase tracking-wider text-fg-subtle font-medium">
          <span>{label}</span>
          <span className="text-fg-subtle">{icon}</span>
        </div>
        <div className="text-2xl font-semibold tabular-nums tracking-tight mt-1">{value}</div>
      </div>
    </button>
  )
}

function StoryRow({ story: s }: { story: MyStory }) {
  const navigate = useNavigate()
  const overdue = s.due_date && new Date(s.due_date + 'T00:00:00') < startOfDay(new Date())

  return (
    <li
      onClick={() => navigate(`/projects/${s.project_key}/backlog?story=${s.key}`)}
      className="px-5 py-2.5 flex items-center gap-3 cursor-pointer hover:bg-bg-muted/40"
    >
      <Chip
        size="sm"
        tone="epic"
        color={s.project_color ?? '#9aa0a6'}
        className="font-mono text-[10px] shrink-0"
      >
        {s.project_key}
      </Chip>
      <span className="font-mono text-xs text-fg-subtle w-14 shrink-0">{s.key}</span>
      <span className="text-sm text-fg flex-1 truncate">{s.title}</span>
      {s.epic_name && (
        <Chip size="sm" tone="epic" color={s.epic_color ?? '#9aa0a6'} dot>
          {s.epic_name}
        </Chip>
      )}
      <Chip size="sm" tone={priorityTone(s.priority)}>{s.priority}</Chip>
      <Chip size="sm" tone={statusTone(s.status_category)}>{s.status_name}</Chip>
      {s.estimate_points !== null && (
        <span className="font-mono text-[10px] text-fg-subtle w-6 text-right">{s.estimate_points}</span>
      )}
      {s.due_date && (
        <span className={cn(
          'text-[10px] w-14 text-right',
          overdue ? 'text-danger font-semibold' : 'text-fg-subtle',
        )}>
          {fmtDue(s.due_date)}
        </span>
      )}
      <ChevronRight size={14} className="text-fg-disabled" />
    </li>
  )
}

/** Today's slice of the personal calendar: events, scheduled/due stories, Google. */
function TodayAgenda({ now }: { now: Date }) {
  const navigate = useNavigate()
  const today = ymd(now)
  const agendaQ = useQuery({
    queryKey: ['agenda', today, today, 'my-work'],
    queryFn: () => getAgenda({ from: today, to: today, include_google: true }),
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  })
  const items = agendaQ.data?.items ?? []

  function open(it: AgendaItem) {
    if (it.story) navigate(`/projects/${it.story.project_key}/backlog?story=${it.story.key}`)
    else if (it.event) navigate(`/calendar?date=${today}&event=${it.event.id}`)
    else if (it.google?.html_link) window.open(it.google.html_link, '_blank', 'noreferrer')
  }

  return (
    <Card padded={false}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div>
          <h3 className="text-sm font-semibold tracking-tight flex items-center gap-1.5">
            <Calendar size={13} className="text-accent" /> Today
          </h3>
          <p className="text-[11px] text-fg-subtle mt-0.5">
            {agendaQ.isLoading ? '…' : `${items.length} ${items.length === 1 ? 'item' : 'items'} across your projects`}
          </p>
        </div>
        <Link to="/calendar" className="text-xs text-accent hover:underline">Calendar →</Link>
      </div>
      {agendaQ.isError && (
        <div className="px-4 py-4 text-xs text-danger">Couldn't load today's schedule.</div>
      )}
      {!agendaQ.isLoading && !agendaQ.isError && items.length === 0 && (
        <div className="px-4 py-6 text-xs text-fg-subtle italic text-center">Nothing on the calendar today.</div>
      )}
      <ul className="divide-y divide-border">
        {items.map(it => {
          const start = new Date(it.start_at.replace(' ', 'T'))
          const end = it.end_at ? new Date(it.end_at.replace(' ', 'T')) : null
          const past = !it.all_day && (end ?? start) < now
          const current = !it.all_day && start <= now && end !== null && end > now
          const color = it.project?.color ?? it.color ?? (it.kind === 'google' ? '#4285f4' : '#9aa0a6')
          return (
            <li key={it.id}>
              <button
                onClick={() => open(it)}
                className={cn(
                  'w-full text-left px-4 py-2 flex items-start gap-3 hover:bg-bg-muted/40',
                  past && 'opacity-50',
                  current && 'bg-accent-soft/30',
                )}
              >
                <span className="font-mono text-[10px] text-fg-subtle w-14 shrink-0 pt-0.5">
                  {it.all_day ? (it.kind === 'story_due' ? 'due' : 'all day') : fmtClock(start)}
                </span>
                <span className="w-0.5 self-stretch rounded-full shrink-0" style={{ backgroundColor: color }} />
                <span className="flex-1 min-w-0">
                  <span className="text-xs font-medium block truncate">
                    {it.kind === 'google' && <Lock size={9} className="inline mr-1 text-fg-subtle" />}
                    {it.title}
                  </span>
                  <span className="text-[10px] text-fg-subtle">
                    {it.project ? it.project.key : it.kind === 'google' ? (it.google?.calendar_name ?? 'Google') : 'Personal'}
                    {end && !it.all_day && ` · until ${fmtClock(end)}`}
                    {current && ' · now'}
                  </span>
                </span>
              </button>
            </li>
          )
        })}
      </ul>
    </Card>
  )
}

// ─── helpers ─────────────────────────────────────────────────────────────

function startOfDay(d: Date): Date {
  const x = new Date(d); x.setHours(0, 0, 0, 0); return x
}
function isThisWeek(iso: string): boolean {
  const d = new Date(iso + 'T00:00:00')
  const today = startOfDay(new Date())
  const week = new Date(today); week.setDate(week.getDate() + 7)
  return d >= today && d < week
}
function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}
function fmtClock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
}
function fmtDue(iso: string): string {
  return new Date(iso + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
